import type DateRange from '../../lib/dateRange';
import { type Measure, type MetricSeriesResult } from '../../types';

import { setupBatchCalculation, processBatchResults, executeBatchedQuery } from './core';

/**
 * Batch calculation for average measures across multiple reporting periods.
 * Reduces query count from 2*N to 1 query for N reporting periods.
 */

/**
 * Calculates average measures using batch queries for multiple reporting periods.
 * This is the main optimization that reduces N*2 queries to 1 query.
 */
export const calculateBatchedAverageMeasures = async (
  measure: Measure | null,
  reportingPeriods: DateRange[],
): Promise<Omit<MetricSeriesResult, 'date'>[]> => {
  const setup = setupBatchCalculation(measure, reportingPeriods);

  if ('shouldReturn' in setup) {
    return reportingPeriods.map(() => ({ errors: { ...setup.errors }, result: { value: null } }));
  }

  const {
    errors,
    adjustedReportingPeriods,
    column,
    dateColumn,
    calculationWhereClause,
    periodSize,
    dataProvider,
    fiscalYearStartMonth,
  } = setup;

  try {
    // Execute single batched query using core function
    const batchData = await executeBatchedQuery(
      dataProvider,
      column,
      dateColumn,
      calculationWhereClause,
      adjustedReportingPeriods,
      periodSize,
      'average',
      fiscalYearStartMonth,
    );

    return processBatchResults(batchData, adjustedReportingPeriods, periodSize, fiscalYearStartMonth, errors);
  } catch (error) {
    console.warn('Batch average calculation failed:', error);
    throw error;
  }
};
